import React from 'react'
import {IApplicationFormData, IWizardCardReadiness, IWizardReviewSummary} from '../../../models/application-form'
import {
  formatAustralianMobile,
  getApplicantName,
  getApplicantTypeSummary,
  toStatusLabel,
} from '../renterHomeUtils'
import {dateFormatDDMMMYYYY} from '../../../../../shared/service/utils'
import {
  normalizeRetentionPreference,
  RETENTION_PREFERENCE_OPTIONS,
  RetentionPreferenceValue,
} from '../../../models/retention-preference'
import {UPLOAD_SLOT_TYPES} from '../../steps/sections/uploadSlots'
import {getCardRequirementStatus} from '../../steps/reviewChecklist'

type ProfileTabProps = {
  summary: IWizardReviewSummary
  draft: IApplicationFormData | null
  onEditSection: (step: string, scrollTarget?: string) => void
  onRetentionPreferenceChange: (value: RetentionPreferenceValue) => void
  isSavingRetentionPreference: boolean
  onSettingAction: (feature: string) => void
  onDeleteEverything: () => void
  onSignOut: () => void
  isPrimaryApplicant: boolean
  primaryApplicant:  IApplicationFormData | null
}

type DooraCardSection = {
  label: string
  detail: string
  step: string
  scrollTarget?: string
  isComplete?: boolean
}

const ProfileTab: React.FC<ProfileTabProps> = ({
  summary,
  draft,
  onEditSection,
  onRetentionPreferenceChange,
  isSavingRetentionPreference,
  onSettingAction,
  onDeleteEverything,
  onSignOut,
  isPrimaryApplicant,
  primaryApplicant
}) => {
  const sections = getDooraCardSections(summary, draft, isPrimaryApplicant, primaryApplicant)
  const retentionPreference = normalizeRetentionPreference(draft?.retentionPreference)
  const missingCriticalFields = (summary.missingCriticalFields ?? []).filter(
    (field) => isPrimaryApplicant || field !== 'retentionPreference'
  )

  return (
    <section className='renter-home-section renter-doora-card-section'>
      <div className='renter-home-section-header'>
        <div>
            <h2>Your Doora Card</h2>
            <p>Review and edit any section of your application.</p>
        </div>
      </div>

      <div className='renter-doora-card-panel'>
        {sections.map((section, index) => (
          <div key={section.label} className='renter-card-summary-row'>
            <div
              className={`renter-card-check ${
                section.isComplete || (!isPrimaryApplicant && section.step === 'preferences')
                  ? ''
                  : section.isComplete === undefined ? '' : 'incomplete'
              }`}
            >
              {section.isComplete || (!isPrimaryApplicant && section.step === 'preferences') ? (
                <svg
                  role='img'
                  aria-label={`${section.label}: Complete`}
                  width='14'
                  height='14'
                  viewBox='0 0 20 20'
                  fill='none'
                  stroke='#0A7C5A'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M3.5 10.5l4 4 9-9'></path>
                </svg>
              ) : section.isComplete === undefined ? (
                <span className='text-muted' aria-label='Status unavailable'>...</span>
              ) : (
                <svg
                  role='img'
                  aria-label={`${section.label}: Incomplete`}
                  width='14'
                  height='14'
                  viewBox='0 0 20 20'
                  fill='none'
                  stroke='#B45309'
                  strokeWidth='2.4'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <circle cx='10' cy='10' r='7'></circle>
                  <path d='M10 6v4l2.5 2'></path>
                </svg>
              )}
            </div>
            <div className='info'>
              <div className='title'>{section.label}</div>
              <div className='detail'>{section.detail}</div>
            </div>

            {isPrimaryApplicant ? (
              <button
                type='button'
                className='edit'
                onClick={() => onEditSection(section.step, section.scrollTarget)}
              >
                Edit
              </button>
            ) : (
              <>
                {section.step !== 'application-type' && section.step !== 'preferences' && (
                  <button
                    type='button'
                    className='edit'
                    onClick={() => onEditSection(section.step, section.scrollTarget)}
                  >
                    Edit
                  </button>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {missingCriticalFields.length > 0 && (
        <div className='renter-profile-warning'>
          Complete required items before sending your Doora Card:{' '}
          {missingCriticalFields.map(toStatusLabel).join(', ')}.
        </div>
      )}

      <div className='renter-account-settings'>
                <div className='renter-home-section-header'>
                  <div>
                    <h3>Account Settings</h3>
                    <p>Manage your data, notifications, and account.</p>
                  </div>
                </div>

                <div className={`card date-retention ${isPrimaryApplicant ? '' : 'read-only'}`.trim()}>
                  <div className='card-head'>
                    <div className='card-inner'>
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 20 20'
                        fill='none'
                        stroke='#1F2937'
                        strokeWidth='1.6'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      >
                        <rect x='3' y='4' width='14' height='13' rx='1.5'></rect>
                        <path d='M3 8h14M7 3v2M13 3v2'></path>
                      </svg>
                    </div>
                    <div>
                      <div className='title'>Data Retention</div>
                      <div className='subtitle'>
                        {isPrimaryApplicant
                          ? 'Choose what happens to your card.'
                          : 'Set by the primary renter.'}
                      </div>
                    </div>
                  </div>
                  {RETENTION_PREFERENCE_OPTIONS.map((option) => (
                    <label
                      className={`item ${isPrimaryApplicant ? '' : 'read-only'}`.trim()}
                      key={option.value}
                    >
                      <input
                        type='radio'
                        name='renter-retention'
                        value={option.value}
                        checked={retentionPreference === option.value}
                        disabled={isSavingRetentionPreference || !isPrimaryApplicant}
                        onChange={() => {
                          if (isPrimaryApplicant) {
                            onRetentionPreferenceChange(option.value)
                          }
                        }}
                      />
                      <div className='radio'>
                        <div className='label'>{option.label}</div>
                        <div className='description'>{option.subLabel}</div>
                      </div>
                    </label>
                  ))}
                </div>

                {/*}
                <div className='renter-settings-card'>
                  <div className='renter-settings-card-heading'>
                    <i className='bi bi-calendar3' aria-hidden='true'></i>
                    <div>
                      <h3>Data Retention</h3>
                      <p>How long should we keep your Doora Card?</p>
                    </div>
                  </div>

                  <label className='renter-radio-row'>
                    <input
                      type='radio'
                      name='renter-retention'
                      defaultChecked={retentionPreference === 60}
                      onChange={() => onSettingAction('Data Retention')}
                    />
                    Auto-delete in 60 days
                  </label>
                  <label className='renter-radio-row'>
                    <input
                      type='radio'
                      name='renter-retention'
                      defaultChecked={retentionPreference === 90}
                      onChange={() => onSettingAction('Data Retention')}
                    />
                    Auto-delete in 90 days
                  </label>
                  <label className='renter-radio-row'>
                    <input
                      type='radio'
                      name='renter-retention'
                      defaultChecked={retentionPreference !== 60 && retentionPreference !== 90}
                      onChange={() => onSettingAction('Data Retention')}
                    />
                    Keep until I delete
                  </label>
                </div>*/}

                <ToggleRow
                  icon={
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 20 20'
                      fill='none'
                      stroke='#1F2937'
                      strokeWidth='1.6'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <path d='M5 8.5a5 5 0 1110 0c0 3 1.5 4.5 2 5.5H3c.5-1 2-2.5 2-5.5zM8 16a2 2 0 004 0'></path>
                    </svg>
                  }
                  title='Notifications'
                  description='Get notified when an agent views your card'
                  onChange={() => onSettingAction('Notifications')}
                />
                <ToggleRow
                  icon={
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 20 20'
                      fill='none'
                      stroke='#1F2937'
                      strokeWidth='1.6'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <circle cx='10' cy='10' r='7'></circle>
                      <path d='M10 6v4l2.5 1.5'></path>
                    </svg>
                  }
                  title='Expiry Reminders'
                  description='Remind me before submissions expire'
                  onChange={() => onSettingAction('Expiry Reminders')}
                />
            </div>
            <div className='delete-card'>
                <div className='head'>
                  <div className='icon'>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 20 20'
                      fill='none'
                      stroke='#EF4444'
                      strokeWidth='1.6'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <path d='M4 5.5h12M8 5.5V4a1.5 1.5 0 011.5-1.5h1A1.5 1.5 0 0112 4v1.5M5.5 5.5l.8 10A1.5 1.5 0 007.8 17h4.4a1.5 1.5 0 001.5-1.5l.8-10'></path>
                    </svg>
                  </div>
                  <div className='title'>Manage your DOORA Card and submissions</div>
                </div>
                <div className='body'>
                  Revoke all active property submissions and keep your DOORA Card, or choose to
                  permanently delete your own card.
                </div>
                <button type='button' className='button' onClick={onDeleteEverything}>
                  Delete everything
                </button>
            </div>
      <button type='button' className='renter-sign-out-btn' onClick={onSignOut}>
        <svg
          width='15'
          height='15'
          viewBox='0 0 20 20'
          fill='none'
          stroke='#111827'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <path d='M12 4.5V4a1.5 1.5 0 00-1.5-1.5h-6A1.5 1.5 0 003 4v12a1.5 1.5 0 001.5 1.5h6A1.5 1.5 0 0012 16v-.5M8 10h10M15 7l3 3-3 3'></path>
        </svg>{' '}
        Sign out
      </button>
    </section>
  )
}

type ToggleRowProps = {
  icon: React.ReactNode
  title: string
  description: string
  onChange: () => void
}

const ToggleRow: React.FC<ToggleRowProps> = ({icon, title, description, onChange}) => (
  <div className='renter-toggle-row'>
    <div className='icon' aria-hidden='true'>
      {icon}
    </div>
    <div className='head'>
      <div className='title'>{title}</div>
      <div className='subtitle'>{description}</div>
    </div>
    <div className='toggle-row'>
      <label className='renter-switch'>
        <input type='checkbox' defaultChecked={true} aria-label={title} onChange={onChange} />
        <span aria-hidden='true'></span>
      </label>
    </div>
  </div>
)

const hasTextValue = (value?: string | null): boolean => Boolean(value?.trim())

const hasDateValue = (value?: string | Date | null): boolean => {
  if (!value) {
    return false
  }

  if (value instanceof Date) {
    return !Number.isNaN(value.getTime())
  }

  return value.trim().length > 0
}

const hasEmploymentStepDetails = (employment?: IApplicationFormData['employment']): boolean => {
  if (!employment) {
    return false
  }

  return (
    [
      employment.companyName,
      employment.employmentType,
      employment.employerName,
      employment.employerPhone,
      employment.employerEmail,
      employment.employerReference,
      employment.additionalReferenceName,
      employment.additionalReferencePhone,
      employment.additionalReferenceEmail,
    ].some(hasTextValue) || hasDateValue(employment.startDate)
  )
}

const getDooraCardSections = (
  summary: IWizardReviewSummary,
  draft: IApplicationFormData | null,
  isPrimaryApplicant: boolean,
  primaryApplicant: IApplicationFormData | null
): DooraCardSection[] => {
  const hasEmploymentIncome = draft?.income?.hasEmploymentIncome === true
  const readiness = summary.cardReadiness
  const firstMissingUploadType = readiness?.documentReadiness?.missingTypes[0]
  const isRentalLedgerFirstMissing = firstMissingUploadType === UPLOAD_SLOT_TYPES.rentalLedger

  var sections = [
    {
      label: 'Application Type',
      detail: `${
        isPrimaryApplicant
          ? getApplicantTypeSummary(summary, draft)
          : getApplicantTypeSummary(null, primaryApplicant)
      }`,
      step: 'application-type',
      isComplete: true,
    },
    {
      label: 'Personal Details',
      detail: `${getApplicantName(summary, draft)} - ${formatAustralianMobile(
        summary.mobileNumber || draft?.mobileNumber
      )}`,
      step: 'profile',
      isComplete: getCardRequirementStatus(readiness, ['identity']),
    },
    {
      label: 'Emergency Contact',
      detail:
        [draft?.emergencyContactName, draft?.emergencyContactRelationship]
          .filter(Boolean)
          .join(' - ') || 'Not provided',
      step: 'profile',
      scrollTarget: 'emergency-contact',
      isComplete: getCardRequirementStatus(readiness, ['emergencyContact']),
    },
    {
      label: 'Income',
      detail: getIncomeSummary(draft),
      step: 'income',
      isComplete: getCardRequirementStatus(readiness, ['income']),
    },
    ...(hasEmploymentIncome
      ? [
          {
            label: 'Employment',
            detail: getEmploymentSummary(summary, draft),
            step: 'employment',
            isComplete: getCardRequirementStatus(readiness, ['employment']),
          },
        ]
      : []),
    {
      label: 'Search Preferences',
      detail: isPrimaryApplicant
        ? getSearchPreferenceSummary(draft)
        : getSearchPreferenceSummary(primaryApplicant),
      step: 'preferences',
      isComplete: Boolean(
        draft?.rentWeeklyBudgetMax || draft?.moveInPreferredDate || draft?.moveInAsap
      ),
    },
    {
      label: 'Rental History',
      detail: getRentalHistorySummary(draft, readiness),
      step: 'rental-history',
      isComplete: getCardRequirementStatus(readiness, ['rentalHistory'], [UPLOAD_SLOT_TYPES.rentalLedger]),
    },
    {
      label: 'Pets & Vehicles',
      detail: `${summary.petCount ?? draft?.pets.length ?? 0} pets - ${
        summary.vehicleCount ?? draft?.vehicles.length ?? 0
      } vehicles`,
      step: 'pets-vehicles',
      isComplete: getCardRequirementStatus(readiness, ['petsVehicles']),
    },
    {
      label: 'Documents',
      detail:
        summary.incompleteUploadCount === 0
          ? `${summary.documentCount ?? draft?.documents.length ?? 0} documents on file`
          : `${summary.documentCount ?? draft?.documents.length ?? 0} uploaded · ${
              summary.incompleteUploadCount
            } to upload`,
      step: isRentalLedgerFirstMissing ? 'rental-history' : 'uploads',
      scrollTarget: isRentalLedgerFirstMissing ? 'rental-ledger' : 'first-missing-upload',
      isComplete: getCardRequirementStatus(readiness, ['documents']),
    },
  ]

   return sections;
}

const getIncomeSummary = (draft: IApplicationFormData | null): string => {
  const income = draft?.income
  const totalIncome = income?.income ?? 0
  const values: string[] = []

  if (totalIncome > 0) {
    values.push(`${formatCurrency(totalIncome)}/yr`)
  }

  if (income?.centreAmount) {
    values.push(`Centrelink ${formatCurrency(income.centreAmount)}/fn`)
  }

  return values.length > 0 ? values.join(' - ') : 'Income details pending'
}

const getEmploymentSummary = (
  summary: IWizardReviewSummary,
  draft: IApplicationFormData | null
): string => {
  const employment = draft?.employment
  if (hasEmploymentStepDetails(employment)) {
    return (
      [employment?.employmentType, employment?.companyName].filter(Boolean).join(' - ') ||
      'Employment details added'
    )
  }

  const employmentCount = summary.employmentCount ?? 0
  return employmentCount > 0
    ? `${employmentCount} employment entries`
    : 'Employment details pending'
}

const getSearchPreferenceSummary = (draft: IApplicationFormData | null): string => {
  const leaseTerm = draft?.preferredLeaseTerm || '12 months'
  const budget = draft?.rentWeeklyBudgetMax
    ? `$${draft.rentWeeklyBudgetMax}/wk`
    : draft?.rentWeeklyBudgetMin
      ? `$${draft.rentWeeklyBudgetMin}/wk`
      : 'Budget pending'
  const moveIn = draft?.moveInAsap
    ? 'ASAP'
    : dateFormatDDMMMYYYY(draft?.moveInPreferredDate) || 'Move-in pending'

  return `${leaseTerm} - ${budget} - ${moveIn}`
}

const getRentalHistorySummary = (
  draft: IApplicationFormData | null,
  readiness?: IWizardCardReadiness
): string => {
  if (draft?.previousRentalHistory === false) {
    return 'No previous rental history'
  }

  if (draft?.previousRentalHistory !== true) {
    return 'Rental history pending'
  }

  const documents = readiness?.documentReadiness
  const ledgerStatus = documents?.requiredTypes.includes(UPLOAD_SLOT_TYPES.rentalLedger)
    ? documents.missingTypes.includes(UPLOAD_SLOT_TYPES.rentalLedger)
      ? documents.deferredTypes?.includes(UPLOAD_SLOT_TYPES.rentalLedger)
        ? 'Ledger upload later'
        : 'Ledger missing'
      : 'Ledger uploaded'
    : undefined
  return (
    [draft.previousRentDuration, ledgerStatus]
      .filter(Boolean)
      .join(' - ') || 'Rental history added'
  )
}

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(value)

export default ProfileTab
